import { useSelector } from 'react-redux';

/**
 * Custom hook for Role-Based Access Control (RBAC).
 * 
 * Uses the Redux store to check if the current user has specific permissions or roles.
 * Implements special logic for 'Super Admin' and 'Admin' roles to have full access
 * if they are 'internal' users and 'active'.
 */
const usePermission = () => {
    const { userInfo } = useSelector((state) => state.auth || {});

    // Helper to safely access nested properties
    const userRole = userInfo?.role?.name || userInfo?.roleName; // support both structures just in case
    const userType = userInfo?.type || userInfo?.userType;
    const permissions = userInfo?.permissions || [];
    const status = userInfo?.status;

    // Check if user is a "Super Admin" or "Admin" with "internal" type and "active" status
    const isSuperAdmin =
        (userRole === 'Super Admin' || userRole === 'Admin') &&
        userType === 'internal' &&
        status === 'active';

    /**
     * Check if the user has a specific permission.
     * @param {string} permissionName - The permission string to check (e.g., 'user.create').
     * @returns {boolean} - True if allowed, false otherwise.
     */
    const hasPermission = (permissionName) => {
        if (!userInfo) return false;

        // Super Admins get full access
        if (isSuperAdmin) return true;

        // Check for wildcard permission 
        if (permissions.includes('all') || permissions.includes('*')) return true;

        // Module-level wildcard match (e.g., "issue.all" allows "issue.create")
        const [module] = permissionName.split('.');
        if (module && (permissions.includes(`${module}.all`) || permissions.includes(`${module}.*`))) return true;

        // Standard permission check
        return permissions.includes(permissionName);
    };

    /**
     * Check if the user has a specific role.
     * @param {string} roleName - The role name to match.
     * @returns {boolean} - True if user has the role.
     */
    const hasRole = (roleName) => {
        return userRole === roleName;
    };

    return {
        hasPermission,
        hasRole,
        isSuperAdmin,
        user: userInfo,
        permissions,
        // Aliases for backward compatibility
        can: hasPermission,
        is: hasRole
    };
};

export default usePermission;
